import React, { useState, useEffect } from "react";
import { Snackbar, Alert } from "@mui/material";
import { CircularProgress } from "@mui/material";

import {
    Dialog,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
} from "@mui/material";

interface DuplicateAlbumModalProps {
    open: boolean;
    onClose: () => void;
    originalAlbum: any;
    token: string;
    onDuplicated: () => void;
}

export default function DuplicateAlbumModal({
    open,
    onClose,
    originalAlbum,
    token,
    onDuplicated,
}: DuplicateAlbumModalProps) {
    const [albumName, setAlbumName] = useState("");
    const [width, setWidth] = useState<number>(0);
    const [height, setHeight] = useState<number>(0);
    const [unit, setUnit] = useState("cm");
    const [aspectRatio, setAspectRatio] = useState<number | null>(null);
    const [isDuplicating, setIsDuplicating] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error" | "info">("info");
    const handleSnackbarClose = () => setSnackbarOpen(false);

    const API_URL = process.env.REACT_APP_API_URL;

    useEffect(() => {
        if (open && originalAlbum) {
            setAlbumName((prev) => prev || `${originalAlbum.albumName}_copy`);
            setWidth(originalAlbum.albumSize.width);
            setHeight(originalAlbum.albumSize.height);
            setUnit(originalAlbum.albumSize.unit);
            setAspectRatio(originalAlbum.albumSize.width / originalAlbum.albumSize.height);
        }
    }, [open, originalAlbum]);

    const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
    const divisor = gcd(Math.round(width * 100), Math.round(height * 100));
    const ratioW = Math.round((width * 100) / divisor);
    const ratioH = Math.round((height * 100) / divisor);

    const handleDuplicate = async () => {
        if (isDuplicating || !originalAlbum) return;

        setIsDuplicating(true);
        setSnackbarMessage("Duplicating album...");
        setSnackbarSeverity("info");
        setSnackbarOpen(true);

        try {
            const res = await fetch(`${API_URL}/duplicateAlbum`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    sourceAlbumName: originalAlbum.albumName,
                    newAlbumName: albumName,
                    newWidth: width,
                    newHeight: height,
                    unit,
                }),
            });

            const data = await res.json().catch(() => ({}));

            if (!res.ok || data.error) {
                // Only show error if server explicitly failed
                throw new Error(data.message || data.error || "Duplication failed");
            }

            // Success
            setSnackbarMessage("Album duplicated successfully");
            setSnackbarSeverity("success");
            setSnackbarOpen(true);

            onDuplicated(); // refresh album list
            onClose();      // close modal
        } catch (err: any) {
            // Only show error if truly failed
            setSnackbarMessage(err?.message || "Error duplicating album");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        } finally {
            setIsDuplicating(false);
        }
    };


    if (!originalAlbum) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    fontFamily: "'Manrope', 'Roboto', 'Segoe UI', sans-serif",
                    transform: "scale(0.9)",
                    transformOrigin: "top center",
                },
            }}
        >
            <DialogContent
                sx={{
                    fontFamily: "'Manrope', 'Roboto', 'Segoe UI', sans-serif",
                }}
            >
                <Box display="flex" flexDirection="column" gap={2}>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 600,
                            fontFamily: "'Manrope', 'Roboto', 'Segoe UI', sans-serif",
                        }}
                    >
                        Duplicate Album
                    </Typography>

                    {originalAlbum && (
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{ fontFamily: "'Manrope', 'Roboto', 'Segoe UI', sans-serif" }}
                        >
                            Based to: <strong>{originalAlbum.albumName}</strong> (
                            {originalAlbum.albumSize.width} x {originalAlbum.albumSize.height}
                            {originalAlbum.albumSize.unit})
                        </Typography>
                    )}

                    <TextField
                        label="Album Name"
                        value={albumName}
                        onChange={(e) => setAlbumName(e.target.value)}
                        fullWidth
                        size="small"
                        InputProps={{ sx: { fontFamily: "'Manrope', 'Roboto', 'Segoe UI', sans-serif" } }}
                        InputLabelProps={{ sx: { fontFamily: "'Manrope', 'Roboto', 'Segoe UI', sans-serif" } }}
                    />

                    <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                        <TextField
                            label="Width"
                            type="number"
                            value={width}
                            onChange={(e) => {
                                const newWidth = Number(e.target.value);
                                setWidth(newWidth);
                                if (aspectRatio) {
                                    setHeight(Number((newWidth / aspectRatio).toFixed(2)));
                                }
                            }}
                            size="small"
                            sx={{ width: "110px" }}
                            InputProps={{ sx: { fontFamily: "'Manrope', 'Roboto', 'Segoe UI', sans-serif" } }}
                            InputLabelProps={{ sx: { fontFamily: "'Manrope', 'Roboto', 'Segoe UI', sans-serif" } }}
                        />

                        <TextField
                            label="Height"
                            type="number"
                            value={height}
                            onChange={(e) => {
                                const newHeight = Number(e.target.value);
                                setHeight(newHeight);
                                if (aspectRatio) {
                                    setWidth(Number((newHeight * aspectRatio).toFixed(2)));
                                }
                            }}
                            size="small"
                            sx={{ width: "110px" }}
                            InputProps={{ sx: { fontFamily: "'Manrope', 'Roboto', 'Segoe UI', sans-serif" } }}
                            InputLabelProps={{ sx: { fontFamily: "'Manrope', 'Roboto', 'Segoe UI', sans-serif" } }}
                        />

                        <FormControl
                            size="small"
                            sx={{
                                width: "140px",
                                fontFamily: "'Manrope', 'Roboto', 'Segoe UI', sans-serif",
                            }}
                        >
                            <InputLabel sx={{ fontFamily: "'Manrope', 'Roboto', 'Segoe UI', sans-serif" }}>
                                Unit
                            </InputLabel>
                            <Select
                                value={unit}
                                label="Unit"
                                onChange={(e) => setUnit(e.target.value)}
                                sx={{ fontFamily: "'Manrope', 'Roboto', 'Segoe UI', sans-serif" }}
                            >
                                <MenuItem value="cm">cm</MenuItem>
                                <MenuItem value="inch">inch</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{
                            mt: 1,
                            fontFamily: "'Manrope', 'Roboto', 'Segoe UI', sans-serif",
                        }}
                    >
                       Aspect Ratio: {ratioW}:{ratioH}
                    </Typography>

                </Box>
            </DialogContent>
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={
                    snackbarSeverity === "error" ? 8000 : snackbarSeverity === "success" ? 3000 : 1500
                }
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={snackbarSeverity}
                    sx={{ width: "100%" }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleDuplicate}
                    disabled={isDuplicating}
                    startIcon={isDuplicating ? <CircularProgress color="inherit" size={18} /> : null}
                >
                    {isDuplicating ? "Duplicating..." : "Duplicate"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
